// src/components/MenuDialog.jsx
// The frame shared by the pop-ups from the menu under your name that came
// in 3.37.0 (Support, Feedback, My notes): title, close button, Escape and a
// click outside to close, focus kept inside while open.
import { useEffect, useRef } from 'react';
import Emo from './Emo';

export default function MenuDialog({ id, icon, title, sub, onClose, width = 760, height = null, headerExtra = null, children, bodyStyle = null }) {
  const closeRef = useRef(null);
  const returnRef = useRef(null);
  // 3.57.5: once, when the window opens (the parent passes a new onClose on
  // every render; re-running moved the cursor to ✕ on every refresh, e.g. a
  // fax pop-up, and the next Space/Enter closed the window)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); returnRef.current?.focus?.(); };
  }, []);
  return (
    <div style={MD.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...MD.modal, width: `min(${width}px, 100%)`, ...(height ? { height } : {}) }} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
        <div style={MD.header} className="dlg-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 id={`${id}-title`} style={MD.title}><Emo e={icon} /> {title}</h2>
            {sub && <div style={MD.sub}>{sub}</div>}
          </div>
          {headerExtra}
          <button ref={closeRef} onClick={onClose} style={MD.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={{ ...MD.body, ...bodyStyle }}>{children}</div>
      </div>
    </div>
  );
}

// Shared bits for the dialogs' contents
export const MD = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)', animation: 'badgePop 0.2s ease-out' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0' },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  sub: { fontSize: 13, color: 'var(--t-78909c)', marginTop: 2 },
  closeBtn: { width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0 },
  body: { overflowY: 'auto', padding: '16px 22px 20px', flex: 1, minHeight: 0 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '14px 0 6px' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' },
  primary: { padding: '10px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  secondary: { padding: '9px 16px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  danger: { padding: '9px 16px', background: 'var(--c-ffffff)', color: 'var(--t-c62828)', border: '1.5px solid var(--c-ef9a9a)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  muted: { color: 'var(--t-90a4ae)', fontSize: 13 },
  error: { background: 'var(--c-ffebee)', color: 'var(--t-c62828)', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginTop: 10 },
};
